/* eslint-disable no-param-reassign */
import { isPlainObject } from 'lodash/fp';

import { IMap } from '../@types/common';
import { JSONType } from '../@types/primitives';

export type AttrValue = string | number | boolean;
export type Attrs = Record<string, AttrValue>;

export const isJsonObject = (value: JSONType): value is IMap<JSONType> => isPlainObject(value);

export const scalar = (key: string, value: AttrValue | null | undefined): Attrs =>
	value === undefined || value === null ? {} : { [key]: value };

export const jsonList = <T>(key: string, items?: readonly T[]): Attrs =>
	items && items.length ? { [key]: JSON.stringify(items) } : {};

type OmitDeepPredicate = readonly string[] | ((value: unknown, key: string) => boolean);

type RenameDict = Record<string, string | undefined>;

function omitDeepValue(predicate: OmitDeepPredicate, value: unknown): unknown {
	if (!isPlainObject(value)) {
		return value;
	}
	return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
		(result, [key, item]) => {
			const isOmitted =
				typeof predicate === 'function' ? predicate(item, key) : predicate.includes(key);
			if (isOmitted) {
				return result;
			}
			if (Array.isArray(item)) {
				result[key] = item.map((element) =>
					isPlainObject(element) ? omitDeepValue(predicate, element) : element
				);
			} else if (isPlainObject(item)) {
				result[key] = omitDeepValue(predicate, item);
			} else {
				result[key] = item;
			}
			return result;
		},
		{}
	);
}

/**
 * Recursively removes properties from an object. `predicate` is either a list of
 * keys to drop or a `(value, key) => boolean` test. Curried: pass the object
 * separately to get a reusable omitter. Non-objects are returned unchanged.
 */
export function omitDeep<T>(predicate: OmitDeepPredicate, object: T): T;
export function omitDeep(predicate: OmitDeepPredicate): <T>(object: T) => T;
export function omitDeep<T>(predicate: OmitDeepPredicate, object?: T): T | (<U>(object: U) => U) {
	if (arguments.length < 2) {
		return <U>(obj: U): U => omitDeepValue(predicate, obj) as U;
	}
	return omitDeepValue(predicate, object) as T;
}

function renameKeysValue(
	renameDict: RenameDict,
	sourceObj: Record<string, unknown>
): Record<string, unknown> {
	return Object.keys(sourceObj).reduce<Record<string, unknown>>((targetObj, originalKey) => {
		if (originalKey in renameDict) {
			const renamedKey = renameDict[originalKey];
			// a falsy mapping target omits the key
			if (renamedKey) {
				targetObj[renamedKey] = sourceObj[originalKey];
			}
		} else {
			targetObj[originalKey] = sourceObj[originalKey];
		}
		return targetObj;
	}, {});
}

/**
 * Renames keys in an object according to `renameDict`; a key mapped to a falsy
 * value is omitted from the result. Curried: pass the object separately to get a
 * reusable renamer.
 */
export function renameKeys(
	renameDict: RenameDict,
	sourceObj: Record<string, unknown>
): Record<string, unknown>;
export function renameKeys(
	renameDict: RenameDict
): (sourceObj: Record<string, unknown>) => Record<string, unknown>;
export function renameKeys(
	renameDict: RenameDict,
	sourceObj?: Record<string, unknown>
): Record<string, unknown> | ((sourceObj: Record<string, unknown>) => Record<string, unknown>) {
	if (arguments.length < 2) {
		return (obj: Record<string, unknown>) => renameKeysValue(renameDict, obj);
	}
	return renameKeysValue(renameDict, sourceObj as Record<string, unknown>);
}
